'use client';

import { useRouter } from 'next/navigation';
import { useState } from 'react';

import CookieService from '@/services/CookieService';
import { useAppDispatch } from '@/store/hooks';
import { clearUser, setIsAuthenticated } from '@/store/reducers/auth';
import { useGoogleLoginMutation, useLoginMutation, useLogoutMutation } from '@/store/rest/authApi';
import { userApi } from '@/store/rest/userApi';
import { EUserTokenNames } from '@/types/user';
import { getMessageFromUnknown } from '@/utils/errorMessage';

import useToast, { TOAST_LABELS } from './useToast';

export const useAuth = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { fromException } = useToast();
  const [loginError, setLoginError] = useState<{ title: string; description: string } | null>(null);

  const [login, { isLoading: isLoginLoading }] = useLoginMutation();
  const [googleLogin, { isLoading: isGoogleLoginLoading }] = useGoogleLoginMutation();
  const [logout] = useLogoutMutation();

  const handleGoogleLogin = async (token: string) => {
    try {
      if (token) {
        const res = await googleLogin({ token }).unwrap();
        CookieService.setCookie(EUserTokenNames.accessToken, res.accessToken);
        dispatch(setIsAuthenticated(true));
        dispatch(userApi.util.resetApiState());
        router.push('/agents');
      } else {
        router.push('/agents');
      }
    } catch (error) {
      setLoginError({ title: TOAST_LABELS.error, description: getMessageFromUnknown(error) });
    }
  };

  const handleSuccessfulLogin = (accessToken: string) => {
    CookieService.setCookie(EUserTokenNames.accessToken, accessToken);
    dispatch(setIsAuthenticated(true));
    dispatch(userApi.util.resetApiState());
    router.push('/agents');
  };

  const handleLogout = async (noToast: boolean = false) => {
    try {
      await logout({}).unwrap();
    } catch (error) {
      if (!noToast) {
        fromException({ error, label: TOAST_LABELS.error });
      }
    } finally {
      dispatch(userApi.util.resetApiState());
      dispatch(setIsAuthenticated(false));
      dispatch(clearUser());
      CookieService.deleteCookie(EUserTokenNames.accessToken);
      router.push('/login');
    }
  };

  return {
    login,
    handleGoogleLogin,
    handleSuccessfulLogin,
    isLoading: isLoginLoading || isGoogleLoginLoading,
    handleLogout,
    loginError,
    setLoginError,
  };
};
